// import React from 'react';
import { authRole } from '@crema/constants/AppConst';
import { RoutePermittedRole } from '@crema/constants/AppEnums';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';
import { checkPermission } from './RouteHelper';

/**
 * @param {Object} structure - The passed object that defines the routes.
 * @param {boolean} structure.isAuthenticated - [Required] in order to differentiate between LoggedIn/Loggedout users
 * @param {string} structure.userRole - [Optional] in order to differentiate between admin and normal users
 * @param {object} [structure.anonymousStructure] - it's an object that has only [ routes ] array, [ these routes available for All personas ]
 * @param {object} [structure.authorizedStructure] - it's an object that has [ fallbackPath: {string}, routes: {array} ], fallbackPath: is used for redirect when a logged [in] user tried to access unAuthorized route, routes: only The Logged [in] Routes Available
 * @param {object} [structure.unAuthorizedStructure] - it's an object that has [ fallbackPath: {string}, routes: {array} ], fallbackPath: is used for redirect when a logged [out] user tried to access route that requires [Authorization] , routes: only The Logged [out] Routes Available
 * @param {component} [structure.component fallbackComponent] - in order to redirect in all cases if the route doesn't match.
 * @param {unAuthorizedComponent} [structure.unAuthorizedComponent] - in order to show not permitted route.
 * @returns {Array} - [Array of routes]
 */

const generateRoutes = (structure: any) => {
  const {
    isAuthenticated = false,
    anonymousStructure = {},
    authorizedStructure = {},
    unAuthorizedStructure = {},
    userRole = authRole.User,
  } = structure || {};

  const dynamicRoutes: RouteObject[] = [];

  if (anonymousStructure) {
    dynamicRoutes.push(...routesGenerator(isAuthenticated, anonymousStructure, 'anonymous'));
  }

  if (authorizedStructure) {
    dynamicRoutes.push(
      ...routesGenerator(isAuthenticated, authorizedStructure, 'authorized', isAuthenticated ? userRole : null),
    );
  }

  if (unAuthorizedStructure) {
    dynamicRoutes.push(...routesGenerator(isAuthenticated, unAuthorizedStructure, 'unAuthorized'));
  }
  return dynamicRoutes;
};

/**
 * path: string
 * component: React.Component
 * routeProps: Object -----> To override route props
 * userRole: string -----> To override route props
 * redirectPath: String ----> To redirect to specific location
 * showRouteIf: to override when to show the component or when to [ Navigate ]
 */
const routesGenerator = (isAuthenticated = false, routeSet: any = {}, type = 'anonymous', userRole?: any): any => {
  const generatedRoutes: any[] = [];
  const { fallbackPath = '' } = routeSet || {};

  const isAnonymous = type === 'anonymous';
  const isAuthorized = type === 'authorized';

  if (routeSet?.routes) {
    const routes = routeSet.routes;
    if (Array.isArray(routes) && routes.length > 0) {
      routes.forEach((route /*index*/) => {
        const {
          path = '',
          permittedRole = RoutePermittedRole.User,
          // routeProps = {},
          redirectPath = '',
          showRouteIf = true,
        } = route || {};
        // Show Route only [ in The list ] if this prop is true
        if (showRouteIf) {
          // check the mandatory props for a routes
          if (!path) {
          } else {
            if (isAnonymous) {
              return generatedRoutes.push(route);
            }
            if (isAuthorized) {
              const renderCondition = isAuthorized ? isAuthenticated : !isAuthenticated;

              if (Array.isArray(route.path)) {
                route.path.map((path: string) => {
                  generatedRoutes.push(
                    renderCondition
                      ? checkPermission(permittedRole, userRole)
                        ? {
                            element: route.element,
                            path: path,
                            permittedRole: route.permittedRole,
                          }
                        : {
                            path: path,
                            element: routeSet.unAuthorizedComponent,
                          }
                      : {
                          path: path,
                          element: <Navigate to={redirectPath || fallbackPath} replace />,
                        },
                  );
                });
              } else {
                generatedRoutes.push(
                  renderCondition
                    ? checkPermission(permittedRole, userRole)
                      ? route
                      : {
                          path: route.path,
                          element: routeSet.unAuthorizedComponent,
                        }
                    : {
                        path: route.path,
                        element: (() => {
                          try {
                            const search = window.location.search || '';
                            const pathname = window.location.pathname || '';
                            // 비밀번호 만료 플래그가 있으면 /password-expired로 유지
                            if (sessionStorage.getItem('passwordExpired') === 'true') {
                              const token = sessionStorage.getItem('passwordExpiredToken') || '';
                              return <Navigate to={`/password-expired?token=${token}`} replace />;
                            }

                            const current = `${pathname}${search}`;
                            const base = redirectPath || fallbackPath;
                            const sep = (base || '').includes('?') ? '&' : '?';
                            return <Navigate to={`${base}${sep}redirect-url=${encodeURIComponent(current)}`} replace />;
                          } catch (e) {
                            return <Navigate to={redirectPath || fallbackPath} replace />;
                          }
                        })(),
                      },
                );
              }
              return generatedRoutes;
            }
            const renderCondition = isAuthorized ? isAuthenticated : !isAuthenticated;
            if (Array.isArray(route.path)) {
              route.path.map((path: string) => {
                generatedRoutes.push(
                  renderCondition
                    ? {
                        element: route.element,
                        path: path,
                        permittedRole: route.permittedRole,
                      }
                    : {
                        path: path,
                        element: <Navigate to={redirectPath || fallbackPath} replace />,
                      },
                );
              });
            } else {
              generatedRoutes.push(
                renderCondition
                  ? route
                  : {
                      path: route.path,
                      element: (() => {
                        try {
                          const search = window.location.search || '';
                          const pathname = window.location.pathname || '';
                          // 비밀번호 만료 플래그가 있으면 /password-expired로 유지
                          if (sessionStorage.getItem('passwordExpired') === 'true') {
                            const token = sessionStorage.getItem('passwordExpiredToken') || '';
                            return <Navigate to={`/password-expired?token=${token}`} replace />;
                          }
                          // 비밀번호 만료/관련 경로에서는 로그인 리다이렉트 금지
                          if (pathname === '/password-expired') {
                            return <Navigate to="/password-expired" replace />;
                          }
                          const current = `${pathname}${search}`;
                          const base = redirectPath || fallbackPath;
                          const sep = (base || '').includes('?') ? '&' : '?';
                          return <Navigate to={`${base}${sep}redirectPath=${encodeURIComponent(current)}`} replace />;
                        } catch (e) {
                          return <Navigate to={redirectPath || fallbackPath} replace />;
                        }
                      })(),
                    },
              );
            }
            return generatedRoutes;
          }
        }
      });
    }
  } else {
  }
  return generatedRoutes;
};

export default generateRoutes;
